import React, {Fragment, useCallback, useMemo, useState} from "react";
import {BottomNavigation, BottomNavigationAction, Box} from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {useTranslation} from "react-i18next";
import Markdown from "react-markdown";
import {useDispatch, useSelector} from "react-redux";
import remarkGfm from "remark-gfm";
import FeatureGuard from "../../hocs/feature-guard";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import AiMetadataIcon from "../custom-icons/AiMetadataIcon";
import {fetchAiAssistantMetadata} from "../../state/ai-assistant/aiAssistantAction";
import {aiEnabledFeaturesSelector, aiMetadataSelector} from "../../state/ai-assistant/aiAssistantSelector";
import {LICENSE_FEATURES_AI} from "../../state/app/contants";
import {catalogSelector} from "../../state/catalog/catalogSelectors";
import {externalServicesSelector} from "../../state/config/configSelectors";
import {nodeIdSelector} from "../../state/node/nodeSelectors";
import {getResourceFromCatalogByType} from "../../utils/catalog";

const AiMetadataButton = ({packageId}) => {
  const {t} = useTranslation();

  const dispatch = useDispatch();

  const nodeId = useSelector(nodeIdSelector);
  const catalog = useSelector(catalogSelector);

  const baseUrl = useSelector(externalServicesSelector)?.ai;
  const ODataBaseUrl = useSelector(externalServicesSelector)?.odata;

  const isMetadataEnabled = useSelector(aiEnabledFeaturesSelector)?.metadata || false;

  const metadata = useSelector(aiMetadataSelector);

  const [isVisible, setVisibility] = useState(false);

  const isMetadataAvailable = useMemo(() => {
    if (!isMetadataEnabled) {
      return false;
    }
    const odataResource = getResourceFromCatalogByType(catalog, packageId, "odata");
    return odataResource !== null;
  }, [catalog, packageId, isMetadataEnabled]);

  const onOpen = useCallback(() => {
    if (!metadata) {
      dispatch(fetchAiAssistantMetadata(nodeId, packageId, ODataBaseUrl, baseUrl));
    }
    setVisibility(true);
  }, [nodeId, packageId, ODataBaseUrl, baseUrl, metadata, dispatch]);

  const onClose = useCallback(() => {
    setVisibility(false);
  }, []);

  return (
    <FeatureGuard featureKey={LICENSE_FEATURES_AI}>
      {isMetadataAvailable && (
        <Fragment>
          <BottomNavigation showLabels onChange={onOpen} style={{marginBottom: 4}}>
            <BottomNavigationAction
              id="ai-assistant__ai-metadata-button__btn"
              label={t("modules.aiAssistant.metadataButton.label")}
              icon={<AiMetadataIcon />}
              role="menuitem"
            />
          </BottomNavigation>

          <CustomDialog open={isVisible && metadata} onClose={onClose}>
            <CustomDialogTitle onClose={onClose}>
              {t("modules.aiAssistant.metadataButton.dialog.title")}
            </CustomDialogTitle>
            <DialogContent>
              <Box
                id="ai-assistant__ai-metadata-button__dialog__content"
                sx={{
                  "& > p:first-child": {
                    marginTop: "0px"
                  },
                  "& > p:last-child": {
                    marginBottom: "0px"
                  }
                }}
              >
                <Markdown remarkPlugins={[remarkGfm]}>{metadata}</Markdown>
              </Box>
              <Box
                id="ai-assistant__ai-metadata-button__dialog__disclaimer"
                sx={{
                  fontSize: "13px",
                  fontStyle: "italic",
                  marginTop: "16px"
                }}
              >
                {t("modules.aiAssistant.metadataButton.dialog.disclaimer")}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>{t("commons.confirm.close")}</Button>
            </DialogActions>
          </CustomDialog>
        </Fragment>
      )}
    </FeatureGuard>
  );
};

export default AiMetadataButton;
